// src/pages/AdminPanel.tsx
// Panel de Administración para gestionar contenido del sitio El Cristo

import React, { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, Link, useLocation } from 'react-router-dom';
import apiElCristo from '../service/api_elcristo';

// Importar componentes del admin
import AdminLogin from '../components/admin/AdminLogin';
import AdminDashboard from '../components/admin/AdminDashboard';
import AdminHeader from '../components/admin/sections/AdminHeader';
import AdminHero from '../components/admin/sections/AdminHero';
import AdminAboutUs from '../components/admin/sections/AdminAboutUs';
import AdminPrograms from '../components/admin/sections/AdminPrograms';
import AdminTeam from '../components/admin/sections/AdminTeam';
import AdminNews from '../components/admin/sections/AdminNews';
import AdminUsers from '../components/admin/AdminUsers';

// CSS del admin
import '../assets/kidsa/admin-elcristo.css';

const AdminPanel: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Verificar si hay sesión activa
    const checkAuth = () => {
      const authenticated = apiElCristo.isAuthenticated();
      setIsAuthenticated(authenticated);
      
      if (authenticated) {
        // Cargar datos del usuario desde localStorage
        try {
          const userData = localStorage.getItem('elcristo_user');
          if (userData) {
            setUser(JSON.parse(userData));
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
          // Si hay error, limpiar localStorage y desautenticar
          localStorage.removeItem('elcristo_user');
          apiElCristo.logout();
          setIsAuthenticated(false);
        }
      }
      
      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleLogin = async (email: string, password: string) => {
    try {
      const result = await apiElCristo.login(email, password);
      setUser(result.user);
      setIsAuthenticated(true);
      localStorage.setItem('elcristo_user', JSON.stringify(result.user));
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const handleLogout = () => {
    apiElCristo.logout();
    localStorage.removeItem('elcristo_user');
    setIsAuthenticated(false);
    setUser(null);
    // Redirigir al sitio principal
    window.location.href = '/elcristo';
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#d5f0ff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="elcristo-spinner"></div>
          <p style={{ marginTop: '20px', color: '#385469', fontWeight: 600 }}>Cargando Panel Administrativo...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="elcristo-admin-panel">
      {/* Header Fijo */}
      <header className="elcristo-admin-header">
        <div className="elcristo-admin-header-left">
          <img 
            src="/assets/kidsa/assets/img/logo/logoheader.png" 
            alt="Logo Colegio El Cristo" 
            className="elcristo-header-logo"
          />
          <div className="elcristo-header-title">
            <h1>Panel de Administración</h1>
            <p>Colegio El Cristo CMS</p>
          </div>
        </div>

        <div className="elcristo-admin-header-right">
          <div className="elcristo-user-info-header">
            <div className="elcristo-user-avatar-small">
              {user?.name?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="elcristo-user-details">
              <span className="elcristo-user-name-header">{user?.name || 'Administrador'}</span>
              <span className="elcristo-user-role-header">{user?.role || 'Admin'}</span>
            </div>
          </div>
          
          <button onClick={handleLogout} className="elcristo-btn-logout-header" title="Volver al sitio">
            <i className="fas fa-sign-out-alt"></i>
            <span>Salir</span>
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <aside className="elcristo-admin-sidebar">
        <nav className="elcristo-sidebar-nav">
          <Link 
            to="/admin/dashboard" 
            className={`elcristo-nav-link ${location.pathname === '/admin/dashboard' || location.pathname === '/admin' || location.pathname === '/admin/' ? 'active' : ''}`}
          >
            <i className="fas fa-home"></i>
            <span>Inicio</span>
          </Link>
          
          <div className="elcristo-nav-section">
            <i className="fas fa-edit"></i>
            <span>Contenido del Sitio</span>
          </div>
          
          <Link 
            to="/admin/header" 
            className={`elcristo-nav-link ${location.pathname === '/admin/header' ? 'active' : ''}`}
          >
            <i className="fas fa-bars"></i>
            <span>Menú & Header</span>
          </Link>
          
          <Link 
            to="/admin/hero" 
            className={`elcristo-nav-link ${location.pathname === '/admin/hero' ? 'active' : ''}`}
          >
            <i className="fas fa-image"></i>
            <span>Portada Principal</span>
          </Link>
          
          <Link 
            to="/admin/aboutus" 
            className={`elcristo-nav-link ${location.pathname === '/admin/aboutus' ? 'active' : ''}`}
          >
            <i className="fas fa-info-circle"></i>
            <span>Nosotros</span>
          </Link>
          
          <Link 
            to="/admin/programs" 
            className={`elcristo-nav-link ${location.pathname === '/admin/programs' ? 'active' : ''}`}
          >
            <i className="fas fa-graduation-cap"></i>
            <span>Programas</span>
          </Link>
          
          <Link 
            to="/admin/team" 
            className={`elcristo-nav-link ${location.pathname === '/admin/team' ? 'active' : ''}`}
          >
            <i className="fas fa-chalkboard-teacher"></i>
            <span>Equipo Docente</span>
          </Link>
          
          <Link 
            to="/admin/news" 
            className={`elcristo-nav-link ${location.pathname === '/admin/news' ? 'active' : ''}`}
          >
            <i className="fas fa-newspaper"></i>
            <span>Noticias</span>
          </Link>
          
          <div className="elcristo-nav-section">
            <i className="fas fa-cog"></i>
            <span>Administración</span>
          </div>
          
          <Link 
            to="/admin/users" 
            className={`elcristo-nav-link ${location.pathname === '/admin/users' ? 'active' : ''}`}
          >
            <i className="fas fa-user-shield"></i>
            <span>Usuarios</span>
          </Link>
        </nav>

        <div className="elcristo-sidebar-footer">
          <div className="elcristo-sidebar-help">
            <i className="fas fa-question-circle"></i>
            <div>
              <p>¿Necesitas ayuda?</p>
              <a href="mailto:soporte@colegioelcristo.cl">Contactar Soporte</a>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="elcristo-admin-main">
        <div className="elcristo-admin-content-wrapper">
          <Routes>
            <Route path="dashboard" element={<AdminDashboard user={user} />} />
            <Route path="header" element={<AdminHeader />} />
            <Route path="hero" element={<AdminHero />} />
            <Route path="aboutus" element={<AdminAboutUs />} />
            <Route path="programs" element={<AdminPrograms />} />
            <Route path="team" element={<AdminTeam />} />
            <Route path="news" element={<AdminNews />} />
            <Route path="users" element={<AdminUsers currentUser={user} />} />
            <Route path="/" element={<AdminDashboard user={user} />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default AdminPanel;